/*
 * form-spam-guard
 * ───────────────
 * Client-side bot filtering for MDJ's public forms (credit application,
 * partner signup). No CAPTCHA: MDJ partners skew older and repeatedly
 * failed the challenge grids, so the project requirement is that a real
 * person is never asked to prove anything.
 *
 * Pairs with snippets/form-spam-guard.liquid. See that file for what
 * each field is and why.
 *
 * WHY A DOCUMENT-LEVEL CAPTURE LISTENER:
 * <heritage-credit-application> and <heritage-customer-signup> both bind
 * their own `submit` handler on the form, and both immediately
 * preventDefault + fire a POST at EasySoft. A listener bound on the form
 * itself would race them (at the target phase, handlers run in
 * registration order regardless of the capture flag). Binding on
 * `document` with capture:true guarantees we run BEFORE any form-level
 * handler, so stopImmediatePropagation() here means the EasySoft POST
 * never happens and Shopify never sees the submission. One chokepoint,
 * no edits to the adapters, and it covers the plain no-adapter form too.
 *
 * WHAT THIS CANNOT DO:
 * Shopify's /contact and /account endpoints accept raw POSTs. A bot that
 * never loads the page never runs this file, so blind POSTs still reach
 * the shop inbox / customer list. This stops bots that render the page —
 * which is every bot that can reach EasySoft, since that path is
 * JS-driven. The `[verified]` field exists so blind POSTs are at least
 * identifiable downstream.
 */
(function () {
  'use strict';

  // The snippet can render once per form; the script tag ships with it,
  // so a page with two guarded forms would otherwise install twice.
  if (window.__mdjFormSpamGuard) return;
  window.__mdjFormSpamGuard = true;

  // hp_client_name is EasySoft's honeypot field name (Imran Qureshi,
  // 2026-09-01). It is forwarded to their API and checked server-side as
  // well as here — do not rename it. The other two are ours.
  var DECOYS = ['website', 'email_confirm', 'hp_client_name'];
  var MIN_ELAPSED_SECONDS = 3;
  var KEY = 'ok';

  function guardsIn(form) {
    return form ? form.querySelectorAll('.spam-guard') : [];
  }

  function field(form, name) {
    return form.querySelector('[name="' + name + '"]');
  }

  /* Stamp load time immediately; arm the proof-of-interaction key only
   * after the visitor actually does something. Headless scripts that
   * fill values directly via the DOM never dispatch trusted events. */
  function arm(form) {
    // SECONDS, not milliseconds — EasySoft parses this server-side and
    // their spec is `Math.floor(Date.now() / 1000)`.
    var ts = field(form, 'hp_time');
    if (ts && !ts.value) ts.value = String(nowSeconds());

    var key = field(form, 'sg_key');
    if (!key || key.value) return;

    var events = ['pointerdown', 'keydown', 'touchstart', 'focusin'];
    var onInteract = function (event) {
      // isTrusted is false for events synthesised by automation that
      // drives the page through script rather than real input.
      if (event && event.isTrusted === false) return;
      key.value = KEY;
      var verified = form.querySelector('[name$="[verified]"]');
      if (verified) verified.value = 'yes';
      events.forEach(function (type) {
        form.removeEventListener(type, onInteract, true);
      });
    };
    events.forEach(function (type) {
      form.addEventListener(type, onInteract, true);
    });
  }

  /* Returns a short reason string when the submission looks automated,
   * or null when it looks human. */
  function inspect(form) {
    for (var i = 0; i < DECOYS.length; i++) {
      var decoy = field(form, DECOYS[i]);
      // Any value at all: a human cannot reach these fields. Zero
      // realistic false positives, so this one is safe to act on alone.
      if (decoy && decoy.value.trim() !== '') return 'decoy:' + DECOYS[i];
    }

    // Everything below depends on arm() having run for THIS form. A
    // stamped hp_time is the proof that it did. Without it we cannot tell
    // "no human touched this" from "our own setup never ran" — and
    // silently dropping a real partner's credit application is far more
    // costly to MDJ than letting one spam message through, so fall back
    // to decoy-only rather than guessing.
    var ts = field(form, 'hp_time');
    if (!ts || !ts.value) return null;

    var key = field(form, 'sg_key');
    if (key && key.value !== KEY) return 'no-interaction';

    var elapsed = nowSeconds() - parseInt(ts.value, 10);
    // Negative/NaN means the value was tampered with, not that the clock
    // moved — treat it the same as impossibly fast.
    if (!(elapsed >= minElapsed(form))) return 'too-fast:' + elapsed + 's';

    return null;
  }

  function nowSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  /* Per-form override via data-min-seconds on the .spam-guard wrapper,
   * so a short signup form can be more forgiving than the long credit
   * application without forking this file. */
  function minElapsed(form) {
    var guard = form.querySelector('.spam-guard');
    var attr = guard && guard.getAttribute('data-min-seconds');
    var seconds = parseFloat(attr);
    return seconds > 0 ? seconds : MIN_ELAPSED_SECONDS;
  }

  /* Fail SILENTLY. The legacy signup alerted "Form not submitted." on a
   * honeypot hit, which tells the operator exactly which field to stop
   * filling. Showing the ordinary success state instead means a bot
   * scores the run as a success and never tunes against us. */
  function swallow(form) {
    var message = form.getAttribute('data-spam-guard-success') ||
      'Thank you — your submission has been received. Our team will follow up shortly.';
    var notice = document.createElement('div');
    notice.className = 'spam-guard-notice';
    notice.setAttribute('role', 'status');
    notice.textContent = message;
    if (form.parentNode) form.parentNode.insertBefore(notice, form);
    form.hidden = true;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !guardsIn(form).length) return;

    var reason = inspect(form);
    if (!reason) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    swallow(form);

    if (window.console && console.debug) {
      console.debug('[form-spam-guard] blocked:', reason);
    }
  }, true);

  function init() {
    var seen = [];
    document.querySelectorAll('.spam-guard').forEach(function (guard) {
      var form = guard.closest('form');
      if (!form || seen.indexOf(form) !== -1) return;
      seen.push(form);
      arm(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
